import React, { useState, useEffect } from 'react';
import { Grid, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Typography, Button, DialogContent, DialogTitle, Avatar } from '@mui/material';
import { CheckCircleRounded, AddRounded, NotificationsActiveRounded } from '@mui/icons-material';
import { grey, red, green } from '@mui/material/colors';
import { RoundedDialog, StandardDialogActions, ItemBox, UserGroup } from '../../Components';
import StateManager from '../../StateManager';
import { FormatDate } from '../../Functions';
import { StatusButton } from '../../../Hubs/tasks/Components';
import { TaskIcon, FormIcon, ProcessIcon } from '../../Icons';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';

export default function ActionDialog({
  open,
  onClose,
  params,
  initial,
  onChange,
  editable,
  columnId,
  rowId,
  onItemsChange,
}) {
  const [items, setItems] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null);
  const processedItems = items.map((item) => ({ ...item, overdue: !!item.dueAt && moment(item.dueAt) < moment() }));
  const [starting, setStarting] = useState(false);
  const history = useHistory();
  const type = params?.action;

  useEffect(() => {
    setItems(Array.isArray(initial) ? initial : []);
  }, [initial, params, open]);

  function saveTask(task) {
    if (!task?._id) return;
    const index = items.findIndex((x) => x._id === task._id);
    if (index === -1) return;
    items[index] = task;
    setItems([...items]);
    onItemsChange(items);
  }

  function onItemClick(item) {
    if (params?.action === 'task') {
      StateManager.selectTask(item._id, (t) => saveTask(t));
    }
    if (params?.action === 'form') {
      history.push(`/forms/entry/${item._id}`);
    }
    if (params?.action === 'process') {
      history.push(`/processes/ongoing/${item._id}`);
    }
  }

  function startEntry() {
    setStarting(true);
    axios
      .post('/forms/entries/sendEntry', { formId: params.form._id, rowId })
      .then((res) => {
        StateManager.setSuccessAlert('Entry has been started');
        if (res.data.length === 1) {
          onChange([...items.map((x) => x._id), res.data[0]._id]);
          setTimeout(() => history.push(`/forms/entry/${res.data[0]._id}`), 1000);
        }
      })
      .catch(() => {
        StateManager.setErrorAlert('Failed to start an entry');
        setStarting(false);
      });
  }

  function createTask() {
    if (starting) return;
    setStarting(true);
    axios
      .post('/tasks/createTask', { ...params.task, rowId })
      .then((res) => {
        setStarting(false);
        StateManager.setSuccessAlert('Task has been created');
        StateManager.selectTask(res.data._id);
        onChange([...items.map((x) => x._id), res.data._id]);
      })
      .catch((err) => {
        setStarting(false);
        StateManager.setAxiosErrorAlert(err);
      });
  }

  function startProcess() {
    setStarting(true);
    axios
      .post('/process/startProcess', { processId: params.process._id, rowId })
      .then((res) => {
        StateManager.setSuccessAlert('Process has been started');
        onChange([...items.map((x) => x._id), res.data.ongoingProcess._id]);
        setTimeout(() => history.push(`/processes/ongoing/${res.data.ongoingProcess._id}?started=true`), 1000);
      })
      .catch(() => {
        StateManager.setErrorAlert('Failed to start the process');
        setStarting(false);
      });
  }

  function addItem() {
    if (params?.action === 'task') {
      createTask();
    }
    if (params?.action === 'form') {
      startEntry();
    }
    if (params?.action === 'process') {
      startProcess();
    }
  }

  function sendReminder(entry) {
    if (sendingReminder) return;
    setSendingReminder(entry?._id);
    axios
      .post('/forms/entries/sendFormReminder', { entryId: entry?._id, registerColumnId: columnId })
      .then((res) => {
        setSendingReminder(false);
        StateManager.setSuccessAlert('Reminder has been sent');
      })
      .catch((err) => {
        setSendingReminder(false);
        StateManager.setAxiosErrorAlert(err);
      });
  }

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth={type === 'task' ? 'md' : 'sm'} fullWidth>
      <DialogTitle>Actions</DialogTitle>
      <DialogContent>
        <Grid container>
          {processedItems.length === 0 && (
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No actions have been caried out yet
            </Typography>
          )}
          {processedItems.map((item, i) => (
            <ItemBox key={i} onClick={() => onItemClick(item)}>
              {type === 'task' && (
                <Grid container alignItems="center" wrap="nowrap">
                  <Grid container item xs={6} alignItems="center" wrap="nowrap">
                    <Grid item>
                      <Avatar style={{ background: grey[200] }}>
                        <TaskIcon />
                      </Avatar>
                    </Grid>
                    <Grid item style={{ marginLeft: 12 }}>
                      <Grid container item>
                        <Typography gutterBottom style={{ fontWeight: 500 }}>
                          {item.title}
                        </Typography>
                      </Grid>
                      <Grid container item direction="column">
                        <Typography gutterBottom color="textSecondary" style={{ fontSize: 14, marginRight: 16 }}>
                          Sent {FormatDate(item.createdAt)}
                        </Typography>

                        {item.dueAt && (
                          <Typography
                            color="textSecondary"
                            style={{
                              fontSize: 14,
                              fontWeight: item.overdue ? 500 : 400,
                              color: item.overdue ? red[600] : undefined,
                            }}
                          >
                            Due {FormatDate(item.dueAt)}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container item xs={6} justifyContent="space-between" alignItems="center" wrap="nowrap">
                    <UserGroup ids={item.assignedUsers} />
                    <StatusButton status={item.progress} isDisabled />
                  </Grid>
                </Grid>
              )}
              {type === 'form' && (
                <Grid container alignItems="center" wrap="nowrap">
                  <Grid container item xs={9} alignItems="center" wrap="nowrap">
                    <Grid item>
                      <Avatar style={{ background: grey[200] }}>
                        <FormIcon />
                      </Avatar>
                    </Grid>
                    <Grid item style={{ marginLeft: 12 }}>
                      <Grid container item>
                        <Typography gutterBottom style={{ fontWeight: 500 }}>
                          {item.title}
                        </Typography>
                      </Grid>
                      <Grid container item direction="column">
                        <Typography gutterBottom color="textSecondary" style={{ fontSize: 14, marginRight: 16 }}>
                          Sent {FormatDate(item.createdAt)}
                        </Typography>

                        {item.dueAt && !item.completedAt && (
                          <Typography
                            color="textSecondary"
                            style={{
                              fontSize: 14,
                              fontWeight: item.overdue ? 500 : 400,
                              color: item.overdue ? red[600] : undefined,
                            }}
                          >
                            Due {FormatDate(item.dueAt)}
                          </Typography>
                        )}
                        {item.completedAt && (
                          <Typography
                            color="textSecondary"
                            style={{
                              fontSize: 14,
                              fontWeight: item.overdue ? 500 : 400,
                              color: item.overdue ? red[600] : undefined,
                            }}
                          >
                            Completed {FormatDate(item.completedAt)}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
                    {item.invitationId && (
                      <Tooltip title="Send reminder">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            sendReminder(item);
                          }}
                          style={{ marginRight: 8 }}
                        >
                          {sendingReminder === item._id ? (
                            <CircularProgress size={24} color="primary" />
                          ) : (
                            <NotificationsActiveRounded style={{ color: grey[500] }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    <CheckCircleRounded style={{ color: item.completedAt ? green[500] : grey[500] }} />
                  </Grid>
                </Grid>
              )}
              {type === 'process' && (
                <Grid container alignItems="center" wrap="nowrap">
                  <Grid container item xs={10} alignItems="center" wrap="nowrap">
                    <Grid item>
                      <Avatar style={{ background: grey[200] }}>
                        <ProcessIcon />
                      </Avatar>
                    </Grid>
                    <Grid item style={{ marginLeft: 12 }}>
                      <Grid container item>
                        <Typography gutterBottom style={{ fontWeight: 500 }}>
                          {item.title}
                        </Typography>
                      </Grid>
                      <Grid container item>
                        <Typography color="textSecondary" style={{ fontSize: 14, marginRight: 16 }}>
                          Sent {FormatDate(item.createdAt)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container item xs={2} justifyContent="flex-end" alignItems="center">
                    <CheckCircleRounded style={{ color: item.completedAt ? green[500] : grey[500] }} />
                  </Grid>
                </Grid>
              )}
            </ItemBox>
          ))}
        </Grid>
      </DialogContent>
      <StandardDialogActions
        additionalActions={
          editable ? (
            <Button style={{ marginRight: 'auto', color: grey[700] }} startIcon={<AddRounded />} onClick={addItem}>
              add {params?.action === 'task' ? 'task' : 'entry'}
            </Button>
          ) : null
        }
        saving={starting}
        onClose={onClose}
      />
    </RoundedDialog>
  );
}
